import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { CityFormData } from '@/components/trips/form/CityForm';
import type { ExpensesFormData } from '@/components/trips/form/ExpensesForm';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';
import { mapTripDetailCity } from '@/lib/api/adapters/trips';
import type { TripDetail, TripDetailCity, TripCityDetail } from '@/hooks/trips/types';

interface SaveCityParams {
  tripId: string;
  cityData: CityFormData;
  cityId?: number;
  expensesData?: ExpensesFormData;
}

async function saveCity({
  tripId,
  cityData,
  cityId,
  expensesData,
}: SaveCityParams): Promise<TripDetailCity> {
  void expensesData; // Expenses handled by dedicated endpoints

  const isUpdate = cityId !== undefined;
  const endpoint = isUpdate
    ? `/api/v1/business-trip-requests/${tripId}/cities/${cityId}`
    : `/api/v1/business-trip-requests/${tripId}/cities`;

  const basePayload = {
    country_id: Number(cityData.countryId),
    city_id: Number(cityData.cityId),
    arrival_date: format(cityData.arrivalDate, 'yyyy-MM-dd'),
    departure_date: format(cityData.departureDate, 'yyyy-MM-dd'),
    need_hotel: cityData.needsAccommodation,
    need_transportation: cityData.needsTransport,
    need_fuel: cityData.needsFuel,
  };

  const payload = basePayload;

  const response = await apiFetch(endpoint, {
    method: isUpdate ? 'PUT' : 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
        ? String(
            (body as Record<string, { message?: string }>).error?.message ?? 'Failed to save city'
          )
        : 'Failed to save city';
    throw new Error(message);
  }

  return mapTripDetailCity(body);
}

function mergeCityCollections<T extends TripDetailCity | TripCityDetail | undefined>(
  base: TripDetailCity,
  previous: T
): TripCityDetail {
  const prevHotels = previous?.hotels ?? [];
  const prevTransportations = previous?.transportations ?? [];
  const prevFuels = previous?.fuels ?? [];

  return {
    ...previous,
    ...base,
    hotels: base.hotels && base.hotels.length > 0 ? base.hotels : prevHotels,
    transportations:
      base.transportations && base.transportations.length > 0
        ? base.transportations
        : prevTransportations,
    fuels: base.fuels && base.fuels.length > 0 ? base.fuels : prevFuels,
  };
}

export function useSaveCity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveCity,
    onSuccess: (city, { tripId, cityId }) => {
      queryClient.setQueryData<TripDetailCity[]>(queryKeys.tripCities(tripId), (old) => {
        if (!old) {
          return cityId !== undefined ? old : [city];
        }

        if (cityId !== undefined) {
          return old.map((existing) =>
            existing.id === city.id ? mergeCityCollections(city, existing) : existing
          );
        }

        return [...old, city];
      });

      if (cityId !== undefined) {
        queryClient.setQueryData<TripCityDetail | TripDetailCity | undefined>(
          queryKeys.tripCity(tripId, cityId),
          (previous) => mergeCityCollections(city, previous)
        );
      }

      queryClient.setQueryData<TripDetail | undefined>(queryKeys.trip(tripId), (old) => {
        if (!old) {
          return old;
        }

        const existingCities = old.cities ?? [];
        const updatedCities = existingCities.some((existing) => existing.id === city.id)
          ? existingCities.map((existing) =>
              existing.id === city.id ? mergeCityCollections(city, existing) : existing
            )
          : [...existingCities, city];

        return {
          ...old,
          cities: updatedCities,
          cityCount: updatedCities.length,
        };
      });

      queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
    },
  });
}
